import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { BlogBreadcrumb } from "@/components/seo/BlogBreadcrumb";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getRelatedPosts } from "@/content/blogPosts";
import { Button } from "@/components/ui/button";
import { CalendarDays, CheckCircle2, ShieldCheck, Bus, Baby, MapPin } from "lucide-react";
import heroImage from "@/assets/tubing/family-tubing-fun.jpg";

const SLUG = "stress-free-family-tubing-guadalupe";
const CANONICAL = `/blog/${SLUG}`;
const TITLE = "Stress-Free Family Tubing on the Guadalupe";
const DESCRIPTION =
  "Skip long walks, crowded shuttles and unknown parking. Shuttle-assisted, limited-capacity tubing at Son's Guadalupe turns chaos into a calm family day.";
const PUBLISH_DATE = "May 30, 2026";

const FAQS = [
  {
    q: "What makes tubing at Son's different from public tubing on the Guadalupe?",
    a: "Three things: (1) the float ends at your private cabana — no half-mile walk to a parking lot, (2) onsite parking is included so you skip the offsite shuttle lots, and (3) daily capacity is limited so the river never feels packed.",
  },
  {
    q: "How long is the float and how does the shuttle work?",
    a: "A typical float runs 2–3 hours on a calm, family-friendly stretch. Our shuttle picks you up at the upstream put-in and you float right back down to your own cabana at Son's — no waiting in line to get back to your car.",
  },
  {
    q: "What's the minimum age for tubing the Guadalupe with us?",
    a: "Kids 5 and up usually do great in their own tube with a properly fitted life jacket. Younger kids ride on a parent's lap or in a connected tube. Our team will flag conditions if it's not a good float day for little ones.",
  },
  {
    q: "Are life jackets included?",
    a: "Life jackets are available on-site. We strongly recommend one for every kid and for any adult who isn't a confident swimmer.",
  },
  {
    q: "Do we have to walk far from the river back to our car?",
    a: "No — that's the whole point of the Son's setup. Your float ends at your cabana. From there it's a few steps to your car in your dedicated parking spot.",
  },
  {
    q: "What should we pack for a stress-free family float?",
    a: "Reef-safe sunscreen, water shoes, life jackets, snacks, a soft cooler, a dry bag for phones, and towels. Leave glass containers and styrofoam at home — both are prohibited on the river.",
  },
];

const schemaJson = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://sonsguadalupe.com/blog" },
        { "@type": "ListItem", position: 3, name: TITLE, item: `https://sonsguadalupe.com${CANONICAL}` },
      ],
    },
    {
      "@type": "BlogPosting",
      headline: "Stress-Free Family Tubing on the Guadalupe — A Parent's Playbook",
      description: DESCRIPTION,
      image: "https://sonsguadalupe.com/og-image.png",
      author: { "@type": "Organization", name: "Son's Guadalupe" },
      datePublished: "2026-05-30",
      mainEntityOfPage: { "@type": "WebPage", "@id": `https://sonsguadalupe.com${CANONICAL}` },
      keywords:
        "stress free family tubing Guadalupe River, shuttle assisted tubing New Braunfels, family tubing with kids, private cabana tubing, limited capacity tubing Guadalupe",
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
  ],
};

const BlogStressFreeFamilyTubing = () => {
  const related = getRelatedPosts(SLUG, 3);

  return (
    <div className="min-h-screen">
      <SEOHead title={TITLE} description={DESCRIPTION} canonical={CANONICAL} schemaJson={schemaJson} />
      <Header />

      <main>
        <article className="container-narrow mx-auto px-4 md:px-8 pt-28 pb-16">
          <BlogBreadcrumb title="Stress-Free Family Tubing" />

          <header className="mb-8">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-charcoal mb-4">
              Stress-Free Family Tubing on the Guadalupe — A Parent's Playbook
            </h1>
            <p className="flex items-center gap-2 text-sm text-charcoal/60">
              <CalendarDays className="w-4 h-4" /> Published {PUBLISH_DATE}
            </p>
          </header>

          <img
            src={heroImage}
            alt="Family floating the Guadalupe River with their private cabana visible on the bank"
            className="w-full h-auto rounded-2xl shadow-soft mb-10"
          />

          <div className="prose prose-lg max-w-none text-charcoal/80">
            <p className="text-xl leading-relaxed">
              Public tubing on the Guadalupe can be magical — or chaotic. Long offsite shuttle lots,
              packed put-ins, and a sweaty half-mile walk back to the car with tired kids. Here's
              how a <strong>curated river experience</strong> at Son's Guadalupe — with
              shuttle-assisted floats and <strong>limited daily capacity for safety and comfort</strong> —
              flips that script.
            </p>

            <h2 className="font-heading text-2xl font-bold text-charcoal mt-10 mb-4 flex items-center gap-2">
              <Bus className="w-6 h-6 text-teal" /> The float ends at your cabana — not a parking lot
            </h2>
            <p>
              At most New Braunfels outfitters, you finish the float at a public exit, then walk (or
              shuttle) back to wherever you parked. With us, the shuttle takes you{" "}
              <em>up</em> the river. You float back down. The takeout is at <em>your</em>{" "}
              cabana — steps from your dedicated parking spot, picnic table, and grill.
            </p>

            <h2 className="font-heading text-2xl font-bold text-charcoal mt-10 mb-4 flex items-center gap-2">
              <Baby className="w-6 h-6 text-teal" /> Why limited capacity matters with kids
            </h2>
            <p>
              We cap how many guests are on the property each day. That means the river doesn't feel
              like a traffic jam, the put-in line is short, and you actually have space to keep eyes
              on your kids. Most weekends, families tell us it feels like a private retreat.
            </p>

            <h2 className="font-heading text-2xl font-bold text-charcoal mt-10 mb-4 flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-teal" /> Built-in safety basics
            </h2>
            <ul>
              <li>Child-safe shallow river entries right at the cabanas</li>
              <li>Life jackets available onsite for every age</li>
              <li>Staff monitoring river flow daily — we'll tell you if it's not a kid day</li>
              <li>Calm, family-friendly stretch — not the rapids further upstream</li>
            </ul>

            <h2 className="font-heading text-2xl font-bold text-charcoal mt-10 mb-4 flex items-center gap-2">
              <MapPin className="w-6 h-6 text-teal" /> The stress-free day, hour by hour
            </h2>
            <ol>
              <li><strong>10 AM</strong> — Arrive, park in your spot, set up at your cabana.</li>
              <li><strong>11 AM</strong> — Quick safety chat, life jackets on, walk to the shuttle.</li>
              <li><strong>11:30 AM</strong> — Put in upstream. Float back down 2–3 hours.</li>
              <li><strong>2 PM</strong> — Float ends <em>at</em> your cabana. Snacks, naps, grill.</li>
              <li><strong>5 PM</strong> — Pack up. Car is 30 feet away. Drive home.</li>
            </ol>

            <h2 className="font-heading text-2xl font-bold text-charcoal mt-12 mb-4">FAQs</h2>
            <div className="space-y-6">
              {FAQS.map((f) => (
                <div key={f.q}>
                  <h3 className="font-heading text-lg font-bold text-charcoal mb-2">{f.q}</h3>
                  <p>{f.a}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 grid sm:grid-cols-2 gap-4">
              <Link to="/tubing">
                <Button variant="hero" size="lg" className="w-full">
                  See tubing details
                </Button>
              </Link>
              <Link to="/book-now/sons-guadalupe">
                <Button variant="hero-outline" size="lg" className="w-full">
                  Book your cabana + tubing
                </Button>
              </Link>
            </div>

            <p className="mt-8 text-sm text-charcoal/60 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-teal" />
              Related reading:{" "}
              <Link to="/blog/tubing-with-kids-guadalupe-river" className="text-teal underline">
                Tubing With Kids — A Parent's Guide
              </Link>
            </p>
          </div>
        </article>

        <RelatedReads posts={related} />
      </main>

      <Footer />
    </div>
  );
};

export default BlogStressFreeFamilyTubing;
